import { useState } from 'react';
import { BookOpen, Play, Check, X, Clock, Upload } from 'lucide-react';
import useStore from '../lib/store';
import { lessons } from '../data/lessons';

export default function LessonPanel() {
  const [selectedLesson, setSelectedLesson] = useState(null);
  const currentLesson = useStore(state => state.currentLesson);
  const lessonMode = useStore(state => state.lessonMode);
  const loadLesson = useStore(state => state.loadLesson);
  const exitLesson = useStore(state => state.exitLesson);

  const [importedLessons, setImportedLessons] = useState([]);
  const [importing, setImporting] = useState(false);

  // Combine built-in and imported lessons
  const allLessons = [...lessons, ...importedLessons];

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'Beginner':
        return '#10b981';
      case 'Intermediate':
        return '#f59e0b';
      case 'Advanced':
        return '#ef4444';
      default:
        return '#6b7280';
    }
  };

  const handleStartLesson = () => {
    if (selectedLesson) {
      if (confirm(`Start lesson: ${selectedLesson.title}?\n\nThis will clear your current network.`)) {
        loadLesson(selectedLesson);
      }
    }
  };

  const handleExitLesson = () => {
    if (confirm('Exit lesson and return to normal mode?')) {
      exitLesson();
    }
  };

  const handleImportLesson = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setImporting(true);

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const content = e.target?.result;
        if (typeof content !== 'string') {
          throw new Error('Invalid file content');
        }

        let lesson;
        
        // Try JSON first (new format - preferred)
        try {
          lesson = JSON.parse(content);
          console.log('✅ Imported as JSON');
        } catch (jsonError) {
          // If JSON fails, try JavaScript object format (old format)
          console.log('Not JSON, trying JavaScript object format...');
          
          // Clean the lesson code
          let lessonCode = content.trim();
          
          // Remove single-line comments (// ...)
          lessonCode = lessonCode.replace(/\/\/.*$/gm, '');
          
          // Remove multi-line comments (/* ... */)
          lessonCode = lessonCode.replace(/\/\*[\s\S]*?\*\//g, '');
          
          // Remove export statement if present
          lessonCode = lessonCode.replace(/export\s+default\s+/g, '');
          lessonCode = lessonCode.replace(/export\s+/g, '');
          
          // If it's wrapped in array brackets, remove them
          if (lessonCode.startsWith('[')) {
            lessonCode = lessonCode.slice(1, -1).trim();
          }

          // Try to evaluate as JavaScript object
          try {
            // eslint-disable-next-line no-eval
            lesson = eval(`(${lessonCode})`);
            console.log('✅ Imported as JavaScript object');
          } catch (evalError) {
            // Try wrapping in a function to handle edge cases
            try {
              // eslint-disable-next-line no-eval
              lesson = eval(`(() => { return ${lessonCode}; })()`);
              console.log('✅ Imported as JavaScript object (wrapped)');
            } catch (wrappedError) {
              throw new Error(`Failed to parse lesson file. ${evalError.message}`);
            }
          }
        }

        // Validate lesson structure
        if (!lesson || typeof lesson !== 'object') {
          throw new Error('Invalid lesson format');
        }

        if (!lesson.id || !lesson.title) {
          throw new Error('Missing required fields: id or title');
        }

        if (!lesson.topology || !lesson.topology.devices) {
          throw new Error('Missing or invalid topology');
        }

        // Validate devices array
        if (!Array.isArray(lesson.topology.devices)) {
          throw new Error('Invalid topology.devices - must be an array');
        }

        if (lesson.topology.devices.length === 0) {
          throw new Error('Topology has no devices');
        }

        // Check if already imported
        if (allLessons.some(l => l.id === lesson.id)) {
          alert(`Lesson "${lesson.title}" is already loaded!`);
          setImporting(false);
          event.target.value = '';
          return;
        }

        // Ensure connections array exists
        if (!lesson.topology.connections) {
          lesson.topology.connections = [];
        }

        // Add to imported lessons
        setImportedLessons(prev => [...prev, lesson]);
        
        const deviceCount = lesson.topology.devices.length;
        const connectionCount = lesson.topology.connections.length;
        const challengeCount = lesson.challenges?.length || 0;
        
        alert(
          `✅ Lesson imported successfully!\n\n` +
          `📚 ${lesson.title}\n` +
          `🖥️  ${deviceCount} device${deviceCount !== 1 ? 's' : ''}\n` +
          `🔗 ${connectionCount} connection${connectionCount !== 1 ? 's' : ''}\n` +
          `🎯 ${challengeCount} challenge${challengeCount !== 1 ? 's' : ''}`
        );
        
      } catch (error) {
        console.error('Import error:', error);
        alert(
          `❌ Failed to import lesson\n\n` +
          `Error: ${error.message}\n\n` +
          `Make sure the file is a valid .lesson file exported from Lesson Builder.`
        );
      } finally {
        setImporting(false);
        event.target.value = ''; // Reset file input
      }
    };

    reader.onerror = () => {
      alert('❌ Error reading file');
      setImporting(false);
      event.target.value = '';
    };

    reader.readAsText(file);
  };

  // Active lesson view
  if (lessonMode && currentLesson) {
    return (
      <div className="lesson-panel">
        <div className="lesson-header-active">
          <div className="lesson-header-info">
            <BookOpen className="lesson-icon" size={20} />
            <div>
              <div className="lesson-active-title">{currentLesson.title}</div>
              <div className="lesson-active-meta">{currentLesson.curriculum}</div>
            </div>
          </div>
          <button className="btn-exit-lesson" onClick={handleExitLesson}>
            <X size={16} />
            Exit Lesson
          </button>
        </div>

        <div className="lesson-content-active">
          {currentLesson.scenario && (
            <div className="lesson-section lesson-scenario-active">
              <h4>📋 Scenario</h4>
              <p>{currentLesson.scenario}</p>
            </div>
          )}

          <div className="lesson-section">
            <h4>Learning Objectives</h4>
            <ul className="lesson-objectives">
              {currentLesson.objectives.map((objective, index) => (
                <li key={index}>{objective}</li>
              ))}
            </ul>
          </div>

          <div className="lesson-section">
            <h4>Challenges</h4>
            <div className="challenges-list">
              {currentLesson.challenges.map((challenge) => (
                <div key={challenge.id} className="challenge-card">
                  <div className="challenge-header">
                    <div className="challenge-number">{challenge.id}</div>
                    <h5>{challenge.title}</h5>
                  </div>
                  <p className="challenge-description">{challenge.description}</p>
                  
                  {challenge.hint && (
                    <details className="challenge-hint">
                      <summary>💡 Hint</summary>
                      <p>{challenge.hint}</p>
                    </details>
                  )}
                  
                  {challenge.solution && (
                    <code className="challenge-solution">{challenge.solution}</code>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="lesson-instructions">
            <h4>How to complete this lesson:</h4>
            <ol>
              <li>Read each challenge carefully</li>
              <li>Select devices by clicking them</li>
              <li>Use the terminal or config panels</li>
              <li>Use hints if you get stuck</li>
              <li>Press Enter to stop continuous ping (ping -t)</li>
            </ol>
          </div>
        </div>
      </div>
    );
  }

  // Lesson detail view
  if (selectedLesson) {
    return (
      <div className="lesson-panel">
        <div className="lesson-detail-header">
          <button className="btn-back" onClick={() => setSelectedLesson(null)}>
            ← Back to lessons
          </button>
        </div>

        <div className="lesson-detail">
          <div className="lesson-detail-top">
            <h2 className="lesson-detail-title">{selectedLesson.title}</h2>
            
            <div className="lesson-badges">
              <span 
                className="badge-difficulty" 
                style={{ backgroundColor: getDifficultyColor(selectedLesson.difficulty) }}
              >
                {selectedLesson.difficulty}
              </span>
              <span className="badge-duration">
                <Clock size={14} />
                {selectedLesson.duration}
              </span>
            </div>
          </div>

          <div className="lesson-curriculum">
            <BookOpen size={16} />
            {selectedLesson.curriculum}
          </div>

          <p className="lesson-description">{selectedLesson.description}</p>

          {selectedLesson.scenario && (
            <div className="lesson-scenario">
              <h4>📋 Scenario</h4>
              <p>{selectedLesson.scenario}</p>
            </div>
          )}

          <div className="lesson-objectives-preview">
            <h4>Learning Objectives</h4>
            <ul>
              {selectedLesson.objectives.map((objective, index) => (
                <li key={index}>
                  <Check size={16} />
                  {objective}
                </li>
              ))}
            </ul>
          </div>

          <div className="lesson-challenges-preview">
            <h4>Challenges ({selectedLesson.challenges.length})</h4>
            <div className="challenges-preview-list">
              {selectedLesson.challenges.map((challenge) => (
                <div key={challenge.id} className="challenge-preview">
                  <div className="challenge-preview-number">{challenge.id}</div>
                  <span>{challenge.title}</span>
                </div>
              ))}
            </div>
          </div>

          <button className="btn-start-lesson" onClick={handleStartLesson}>
            <Play size={18} />
            Start Lesson
          </button>
        </div>
      </div>
    );
  }

  // Lesson list view
  return (
    <div className="lesson-panel">
      <div className="lesson-list-header">
        <h2>
          <BookOpen size={28} />
          Available Lessons
        </h2>
        <p>Select a lesson to begin learning</p>
      </div>

      <div className="import-lesson-section">
        <h4>Import Custom Lesson</h4>
        <p>Upload a .lesson file created with Lesson Builder</p>
        <div className="file-input-wrapper">
          <button className="btn-import" disabled={importing}>
            <Upload size={18} />
            {importing ? 'Importing...' : 'Choose .lesson File'}
          </button>
          <input
            type="file"
            accept=".lesson"
            onChange={handleImportLesson}
            disabled={importing}
          />
        </div>
      </div>

      {/* Lessons Grid */}
      {/* Custom Lessons Section - Show imported lessons at the top */}
      {importedLessons.length > 0 && (
        <>
          <div className="lessons-section-header">
            <h3>📁 Custom Lessons</h3>
            <span className="lesson-count-badge">{importedLessons.length}</span>
          </div>
          <div className="lessons-grid">
            {importedLessons.map((lesson) => (
              <div
                key={lesson.id}
                className="lesson-card custom-lesson-card"
                onClick={() => setSelectedLesson(lesson)}
              >
                <div className="lesson-card-header">
                  <h3>{lesson.title}</h3>
                  <span className="custom-badge">Custom</span>
                </div>

                <div className="lesson-card-meta">
                  <span 
                    className="badge-difficulty" 
                    style={{ backgroundColor: getDifficultyColor(lesson.difficulty) }}
                  >
                    {lesson.difficulty}
                  </span>
                  <span className="badge-duration">
                    <Clock size={14} />
                    {lesson.duration}
                  </span>
                </div>

                <div className="lesson-card-curriculum">{lesson.curriculum}</div>

                <p className="lesson-card-description">{lesson.description}</p>

                <div className="lesson-card-footer">
                  <span>{lesson.objectives?.length || 0} objectives</span>
                  <span>•</span>
                  <span>{lesson.challenges?.length || 0} challenges</span>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Built-in Lessons Section */}
      {importedLessons.length > 0 && (
        <div className="lessons-section-header">
          <h3>📚 Built-in Lessons</h3>
          <span className="lesson-count-badge">{lessons.length}</span>
        </div>
      )}
      <div className="lessons-grid">
        {lessons.map((lesson) => (
          <div
            key={lesson.id}
            className="lesson-card"
            onClick={() => setSelectedLesson(lesson)}
          >
            <div className="lesson-card-header">
              <h3>{lesson.title}</h3>
            </div>

            <div className="lesson-card-meta">
              <span 
                className="badge-difficulty" 
                style={{ backgroundColor: getDifficultyColor(lesson.difficulty) }}
              >
                {lesson.difficulty}
              </span>
              <span className="badge-duration">
                <Clock size={14} />
                {lesson.duration}
              </span>
            </div>

            <div className="lesson-card-curriculum">{lesson.curriculum}</div>

            <p className="lesson-card-description">{lesson.description}</p>

            <div className="lesson-card-footer">
              <span>{lesson.objectives.length} objectives</span>
              <span>•</span>
              <span>{lesson.challenges.length} challenges</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
